// chatService.js
import { api } from './config';

/**
 * Servicio de chat basado únicamente en llamadas REST al backend (sin WebSockets).
 */
const chatService = {
  /**
   * Envía un nuevo mensaje al chat usando la ruta REST.
   */
  sendMessage: async (messageData) => {
    try {
      const response = await api.post('/chat/send', messageData);
      return response.data;
    } catch (error) {
      console.error('[chatService] Error sending message via REST:', error);
      throw error;
    }
  },

  /**
   * Obtiene el historial completo de una conversación (chatId).
   */
  getConversation: async (chatId) => {
    try {
      const encodedChatId = encodeURIComponent(chatId);
      const response = await api.get(`/chat/conversation/${encodedChatId}`);
      return response.data;
    } catch (error) {
      console.error('[chatService] Error fetching conversation:', error);
      throw new Error(error.response?.data?.error || 'Error al obtener la conversación');
    }
  },

  /**
   * Actualiza el estado de un mensaje, p.ej. "sent" -> "read".
   */
  updateMessageStatus: async (updateData) => {
    try {
      const response = await api.put('/chat/update-status', updateData);
      return response.data;
    } catch (error) {
      console.error('[chatService] Error updating message status:', error);
      throw new Error(error.response?.data?.error || 'Error al actualizar el estado del mensaje');
    }
  },

  /**
   * Obtiene todos los chats del usuario actual.
   */
  getUserChats: async () => {
    try {
      const response = await api.get('/chat/user-chats');
      return response.data;
    } catch (error) {
      console.error('[chatService] Error fetching user chats:', error);
      throw new Error(error.response?.data?.error || 'Error al obtener los chats del usuario');
    }
  },

  /**
   * Marca todos los mensajes de un chat como leídos.
   */
  markChatAsRead: async (chatId) => {
    try {
      const encodedChatId = encodeURIComponent(chatId);
      const response = await api.put(`/chat/mark-read/${encodedChatId}`);
      return response.data;
    } catch (error) {
      console.error('[chatService] Error marking chat as read:', error);
      throw new Error(error.response?.data?.error || 'Error al marcar el chat como leído');
    }
  },

  /**
   * Elimina (soft-delete) un chat para el usuario actual.
   */
  deleteChat: async (chatId) => {
    try {
      const encodedChatId = encodeURIComponent(chatId);
      const response = await api.delete(`/chat/delete/${encodedChatId}`);
      return response.data;
    } catch (error) {
      console.error('[chatService] Error deleting chat:', error);
      throw new Error(error.response?.data?.error || 'Error al borrar el chat');
    }
  },

  /**
   * Inicia una negociación con IA sin historial completo.
   */
  negotiateWithAI: async (chatId, productId, productOwnerId) => {
    try {
      const response = await api.post('/chat/negotiate', {
        chatId,
        productId,
        productOwnerId
      });
      return response.data;
    } catch (error) {
      console.error('[chatService] Error negotiating with AI:', error);
      throw new Error(error.response?.data?.error || 'Error al negociar con IA');
    }
  },

  /**
   * Obtiene una respuesta de IA usando el historial completo.
   * Incluye manejo de límites y estados de negociación.
   */
  replyWithAI: async (chatId, latestUserMessage) => {
    try {
      const response = await api.post('/chat/reply-with-ai', {
        chatId,
        latestUserMessage,
      });
      return response.data;
    } catch (error) {
      console.error('[chatService] Error calling replyWithAI:', error);
      
      // Manejar errores específicos
      if (error.response?.status === 429) {
        throw new Error('Límite de respuestas alcanzado o necesitas esperar antes del siguiente mensaje');
      }
      if (error.response?.status === 400 && error.response.data?.error?.includes('finalizado')) {
        throw new Error('La negociación ya ha finalizado');
      }
      
      throw new Error(error.response?.data?.error || 'Error al obtener respuesta IA contextual');
    }
  },

  /**
   * Verifica el estado actual de la negociación automática.
   * Retorna información sobre límites, progreso y estado.
   */
  checkNegotiationStatus: async (chatId) => {
    try {
      const encodedChatId = encodeURIComponent(chatId);
      const response = await api.get(`/chat/negotiation-status/${encodedChatId}`);
      return response.data;
    } catch (error) {
      console.error('[chatService] Error checking negotiation status:', error);
      throw new Error(error.response?.data?.error || 'Error al verificar estado de negociación');
    }
  },

  /**
   * Helper para verificar si un mensaje indica fin de negociación.
   */
  isNegotiationEndMessage: (message) => {
    const endPhrases = [
      'trato cerrado',
      'negociación finalizada',
      'acuerdo alcanzado',
      'acepto la oferta',
      'confirmo el precio',
      'trato hecho',
      'precio aceptado',
      'cerramos el trato',
      'acepto los términos',
      'estoy de acuerdo con'
    ];
    
    return endPhrases.some(phrase => 
      message.toLowerCase().includes(phrase.toLowerCase())
    );
  },

  /**
   * Formatea un chatId a partir de dos IDs de usuario.
   */
  formatChatId: (senderId, receiverId) => {
    if (!senderId || !receiverId) {
      console.error('[chatService] Invalid user IDs:', { senderId, receiverId });
      throw new Error('Se requieren ambos IDs de usuario para formar el chatId');
    }
    return `${senderId}#${receiverId}`;
  }
};

export default chatService;